/**
 * JOAAT (Jenkins one-at-a-time) hash — the string-hashing algorithm Rockstar
 * uses throughout GTA V / RAGE and therefore FiveM. Model names, weapon names,
 * ped names, audio names, and countless meta references are stored as the JOAAT
 * hash of their **lowercased** string. FiveM's `GetHashKey` / `joaat` natives
 * return exactly this value.
 *
 * Pure, dependency-free, and unit-testable. All arithmetic is coerced back to an
 * unsigned 32-bit integer after every step so the result matches the reference C
 * implementation exactly (e.g. `joaat("weapon_pistol") === 0x1B06D571`).
 */

/**
 * Compute the unsigned 32-bit JOAAT hash of `input`.
 *
 * The input is lowercased first (Rockstar convention) and encoded as UTF-8 so
 * the byte stream matches the engine for any non-ASCII characters. For the
 * overwhelmingly common ASCII case this is identical to hashing char codes.
 */
export function joaat(input: string): number {
  const bytes = utf8Bytes(input.toLowerCase());
  let hash = 0;
  for (let i = 0; i < bytes.length; i++) {
    hash = (hash + bytes[i]) >>> 0;
    hash = (hash + ((hash << 10) >>> 0)) >>> 0;
    hash = (hash ^ (hash >>> 6)) >>> 0;
  }
  hash = (hash + ((hash << 3) >>> 0)) >>> 0;
  hash = (hash ^ (hash >>> 11)) >>> 0;
  hash = (hash + ((hash << 15) >>> 0)) >>> 0;
  return hash >>> 0;
}

/** JOAAT hash formatted as lowercase `0x`-prefixed, zero-padded hex (e.g. `0x1b06d571`). */
export function joaatHex(input: string): string {
  return "0x" + joaat(input).toString(16).padStart(8, "0");
}

/** JOAAT hash as a signed 32-bit integer — the form some tools / DB dumps show. */
export function joaatSigned(input: string): number {
  return joaat(input) | 0;
}

/** All three common representations of a single input, computed once. */
export interface JoaatResult {
  input: string;
  /** The lowercased string actually hashed. */
  normalized: string;
  /** Unsigned 32-bit decimal. */
  unsigned: number;
  /** Signed 32-bit decimal. */
  signed: number;
  /** `0x`-prefixed lowercase hex, zero-padded to 8 digits. */
  hex: string;
}

export function joaatAll(input: string): JoaatResult {
  const normalized = input.toLowerCase();
  const unsigned = joaat(input);
  return {
    input,
    normalized,
    unsigned,
    signed: unsigned | 0,
    hex: "0x" + unsigned.toString(16).padStart(8, "0"),
  };
}

/** Minimal UTF-8 encoder — avoids a hard dependency on a global `TextEncoder`. */
function utf8Bytes(str: string): number[] {
  if (typeof TextEncoder !== "undefined") {
    return Array.from(new TextEncoder().encode(str));
  }
  const out: number[] = [];
  for (let i = 0; i < str.length; i++) {
    let code = str.charCodeAt(i);
    if (code < 0x80) {
      out.push(code);
    } else if (code < 0x800) {
      out.push(0xc0 | (code >> 6), 0x80 | (code & 0x3f));
    } else if (code >= 0xd800 && code <= 0xdbff) {
      // Surrogate pair.
      const hi = code;
      const lo = str.charCodeAt(++i);
      code = 0x10000 + ((hi & 0x3ff) << 10) + (lo & 0x3ff);
      out.push(
        0xf0 | (code >> 18),
        0x80 | ((code >> 12) & 0x3f),
        0x80 | ((code >> 6) & 0x3f),
        0x80 | (code & 0x3f),
      );
    } else {
      out.push(0xe0 | (code >> 12), 0x80 | ((code >> 6) & 0x3f), 0x80 | (code & 0x3f));
    }
  }
  return out;
}
